/**
 * Job Planner Dialog — per-job checklist + notes + email-to-supervisor.
 *
 * Renders a modal users can open from any job detail surface (Jobs page,
 * Kanban card, or dashboard Quick Action). Pulls and persists state on
 * `/api/jobs/:id/checklist` and exposes `Send to admin/supervisor` via
 * `/api/jobs/:id/checklist/email` with a recipient dropdown.
 *
 * Designed to be self-contained — drop it anywhere with `<JobPlannerDialog
 * jobId={id} jobTitle={title} open={bool} onOpenChange={fn} />`.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, GripVertical, Send, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.__BACKEND_URL__}/api`;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function JobPlannerDialog({ jobId, jobTitle, open, onOpenChange }) {
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (!jobId) return;
    try {
      const [chk, rec] = await Promise.all([
        axios.get(`${API}/jobs/${jobId}/checklist`, { headers: authHeaders() }),
        axios.get(`${API}/jobs/${jobId}/checklist/recipients`, { headers: authHeaders() }),
      ]);
      setNotes(chk.data.notes || '');
      setItems((chk.data.items || []).sort((a, b) => (a.order || 0) - (b.order || 0)));
      setRecipients(rec.data.recipients || []);
    } catch (err) {
      console.error('Failed to load planner:', err);
      toast.error('Could not load your planner');
    }
  }, [jobId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const addItem = () => {
    setItems((prev) => [
      ...prev,
      { id: `tmp-${Date.now()}-${prev.length}`, text: '', checked: false, order: prev.length },
    ]);
  };

  const updateItem = (id, patch) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const removeItem = (id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  };

  const moveItem = (idx, dir) => {
    setItems((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((it, i) => ({ ...it, order: i }));
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      // Filter out empty items so we don't pollute the worker's plan.
      const clean = items.filter((it) => (it.text || '').trim()).map((it, i) => ({
        ...it, order: i, text: it.text.trim(),
      }));
      await axios.put(`${API}/jobs/${jobId}/checklist`, { notes, items: clean }, { headers: authHeaders() });
      setItems(clean);
      toast.success('Plan saved');
    } catch (err) {
      console.error(err);
      toast.error('Could not save your plan');
    } finally {
      setSaving(false);
    }
  };

  const sendEmail = async () => {
    if (!selectedRecipient) {
      toast.error('Pick who to send this plan to');
      return;
    }
    setSending(true);
    try {
      // Save first so the recipient receives the latest state.
      await save();
      await axios.post(
        `${API}/jobs/${jobId}/checklist/email`,
        { recipient_user_id: selectedRecipient, message: emailMessage || null },
        { headers: authHeaders() },
      );
      toast.success('Plan sent — your supervisor will see it shortly');
      setEmailMessage('');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not send your plan');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="job-planner-dialog">
        <DialogHeader>
          <DialogTitle>Plan your work — {jobTitle || 'this job'}</DialogTitle>
          <DialogDescription>
            Build a checklist before you start. Organize the steps in the order that makes sense to you,
            then share with your admin or supervisor so they know how you're approaching the work.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-2">
            <Label htmlFor="planner-notes">Notes</Label>
            <Textarea
              id="planner-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Site contact, hazards to watch for, tools to bring..."
              rows={3}
              data-testid="planner-notes"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Checklist</Label>
              <Button size="sm" variant="outline" onClick={addItem} data-testid="planner-add-item">
                <Plus className="w-4 h-4 mr-1" /> Add step
              </Button>
            </div>
            <div className="space-y-2">
              {items.length === 0 ? (
                <p className="text-sm text-slate-500 italic">
                  No steps yet — click "Add step" to start.
                </p>
              ) : (
                items.map((it, idx) => (
                  <div
                    key={it.id}
                    className="flex items-start gap-2 bg-slate-50 dark:bg-slate-900/40 rounded-md p-2 border border-slate-200 dark:border-slate-700"
                  >
                    <div className="flex flex-col gap-0 pt-1">
                      <button type="button" onClick={() => moveItem(idx, -1)} className="text-slate-400 hover:text-slate-700 text-xs leading-none" aria-label="Move up">▲</button>
                      <GripVertical className="w-3 h-3 text-slate-300" />
                      <button type="button" onClick={() => moveItem(idx, 1)} className="text-slate-400 hover:text-slate-700 text-xs leading-none" aria-label="Move down">▼</button>
                    </div>
                    <Checkbox
                      checked={!!it.checked}
                      onCheckedChange={(c) => updateItem(it.id, { checked: !!c })}
                      className="mt-2"
                    />
                    <Input
                      value={it.text}
                      onChange={(e) => updateItem(it.id, { text: e.target.value })}
                      placeholder="Describe this step..."
                      className="flex-1"
                      data-testid={`planner-item-${idx}`}
                    />
                    <Button size="icon" variant="ghost" onClick={() => removeItem(it.id)} aria-label="Remove">
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="border-t border-slate-200 dark:border-slate-700 pt-4 space-y-2">
            <Label>Send this plan to an admin or supervisor</Label>
            <div className="flex gap-2">
              <Select value={selectedRecipient} onValueChange={setSelectedRecipient}>
                <SelectTrigger className="flex-1" data-testid="planner-recipient-select">
                  <SelectValue placeholder="Choose recipient..." />
                </SelectTrigger>
                <SelectContent>
                  {recipients.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name} — {r.role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Textarea
              placeholder="Optional message to include..."
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button onClick={save} disabled={saving} variant="outline" data-testid="planner-save">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save Plan'}
          </Button>
          <Button onClick={sendEmail} disabled={sending || !selectedRecipient} data-testid="planner-send">
            <Send className="w-4 h-4 mr-1" /> {sending ? 'Sending...' : 'Save & Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
